<script setup>
import { computed, ref, watch } from "vue";
import { useUserStore } from "@/_store/userStore.js";
import { vehicleStore } from "@/_store/vehicleStore";
import { ArgonLoadingButton } from "@/components";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import VehicleSelectWithAvatar from "@/components/VehicleSelectWithAvatar.vue";
import ModalBase from "@/components/modal/ModalBase.vue";
import AlertMsg from "@/components/AlertMessage.vue";
import { errorUtils } from "@/utils";

const userStore = useUserStore();
const vehiclesStore = vehicleStore();

const rows = ref([]);
const snackbar = ref(null);
const showValidation = ref(false);
const closeSnackbar = () => {
  snackbar.value = null;
};

const hasEmptyVehicle = computed(() =>
  rows.value.some((row) => !row.vehicle_id)
);

const vehicleOptions = computed(() => {
  const list = Array.isArray(vehiclesStore.allActiveVehicles)
    ? vehiclesStore.allActiveVehicles
    : [];

  return list
    .map((vehicle, index) => {
      return {
        value: vehicle?._id || vehicle?.id || `vehicle_${index}`,
        label: vehicle?.name || vehicle?.label || vehicle?.plate || "Vehículo",
        abbreviation: vehicle?.abbreviation || "",
        color: vehicle?.route_color_hex || vehicle?.color || "",
      };
    })
    .filter((vehicle) => Boolean(vehicle.value));
});

function loadActiveVehicles() {
  return vehiclesStore.getAllActiveVehicles();
}

function setRows(selectedUsers) {
  rows.value = (selectedUsers || []).map((item) => ({
    id: item._id,
    user: item.user,
    vehicle_id: item.extraData?.vehicle_id || "",
  }));
}

function onVehicleSelectedChange(row, option) {
  const vehicle = Array.isArray(option) ? option[0] : option;
  row.vehicle_id = vehicle?.value || "";
}

function submit() {
  showValidation.value = true;
  if (hasEmptyVehicle.value) {
    snackbar.value = {
      title: "Error",
      message: "El vehículo es obligatorio para todos los mensajeros",
      type: "danger",
      color: "white",
    };
    return;
  }

  const items = rows.value.map((row) => ({
    id: row.id,
    vehicle_id: row.vehicle_id,
  }));
  userStore.reassignVehicles(items);
}

function cleanData() {
  rows.value = [];
  showValidation.value = false;
}

watch(
  () => userStore.error,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "Error",
        message: errorUtils.getErrorMessageHtml(value),
        type: "danger",
        color: "white",
      };
    }
  }
);

const disableButton = computed({
  get() {
    return userStore.disableButton;
  },
});

defineExpose({ setRows, loadActiveVehicles });
</script>

<template>
  <modal-base ref="reassignVehiclesModal" width="40rem" @hide="cleanData">
    <template #header>
      <h6 id="ModalLabel" class="modal-title">Reasignar vehículos</h6>
    </template>
    <template #body>
      <alert-msg title="" type="simple" class="mb-3">
        <span
          >Sólo puede haber un mensajero activo por vehículo. Si cambias el
          vehículo de un mensajero, tendrá que volver a iniciar sesión en la
          PDA.</span
        >
      </alert-msg>
      <form class="border-0">
        <div v-for="row in rows" :key="row.id" class="row align-items-center mb-3">
          <div class="col-5">
            <label class="col-form-label mb-0">{{ row.user }}</label>
          </div>
          <div class="col-7">
            <vehicle-select-with-avatar
              :id="`reassign-vehicle-${row.id}`"
              v-model="row.vehicle_id"
              :options="vehicleOptions"
              :filterable="false"
              menu-width="320"
              :error="showValidation && !row.vehicle_id"
              @selected-change="(option) => onVehicleSelectedChange(row, option)"
            />
          </div>
        </div>
        <div v-if="rows.length === 0" class="text-secondary text-sm">
          Selecciona al menos un mensajero para reasignar su vehículo.
        </div>
      </form>
    </template>
    <template #footer>
      <argon-loading-button
        class="btn btn-xs bg-gradient-primary"
        color="primary"
        variant="gradient"
        :label="disableButton ? 'Guardando...' : 'Guardar'"
        :disable-button="disableButton || rows.length === 0"
        @click="submit"
      >
      </argon-loading-button>
    </template>
    <div class="position-fixed top-1 end-1">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
<style lang="scss"></style>
